import {
  getCuratorNicknameChangeCount,
  getCuratorNicknameCheckDuplicate,
  getCuratorProfileBySnsTitle,
  getNicknameRecommendation,
  putCuratorNickname,
  putCuratorSns,
  putProfileImage,
} from '@musinsa/apis-curator';
import { lazyPipe } from '@musinsa/packages-functional';
import {
  validateServiceRequest,
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';

import { isApiError } from '../utils';

import type {
  GetCuratorProfileBySnsTitleServiceRequest,
  GetCuratorProfileBySnsTitleServiceResponse,
  GetNicknameAvailableCountServiceResponse,
  GetNicknameRecommendationServiceResponse,
  UpdateNicknameServiceRequest,
  UpdateNicknameServiceResponse,
  UpdateProfileImageServiceRequest,
  UpdateProfileImageServiceResponse,
  UpdateSnsLinksServiceRequest,
  UpdateSnsLinksServiceResponse,
  ValidateNicknameServiceRequest,
  ValidateNicknameServiceResponse,
} from './my-profile.dto';

import {
  GetCuratorProfileBySnsTitleServiceRequestSchema,
  GetCuratorProfileBySnsTitleServiceResponseSchema,
  GetNicknameAvailableCountServiceResponseSchema,
  GetNicknameRecommendationServiceResponseSchema,
  UpdateNicknameServiceRequestSchema,
  UpdateNicknameServiceResponseSchema,
  UpdateProfileImageServiceRequestSchema,
  UpdateProfileImageServiceResponseSchema,
  UpdateSnsLinksServiceRequestSchema,
  UpdateSnsLinksServiceResponseSchema,
  ValidateNicknameServiceRequestSchema,
  ValidateNicknameServiceResponseSchema,
} from './my-profile.dto';

// NOTE: 프로필 이미지 수정 서비스
export const updateProfileImageService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(UpdateProfileImageServiceRequestSchema),
    async (
      request: UpdateProfileImageServiceRequest,
    ): Promise<UpdateProfileImageServiceResponse> => {
      const { meta, data } = await putProfileImage({
        profileImage: request.profileImage,
      });

      if (meta?.result === 'FAIL') {
        throw new Error(meta.message ?? '프로필 이미지 수정 중 에러가 발생했습니다.');
      }

      // Service Layer DTO로 변환하여 반환
      const response: UpdateProfileImageServiceResponse = {
        curatorId: data.curatorId,
        imageUrl: data.imageUrl,
      };

      return response;
    },
    validateServiceResponse(UpdateProfileImageServiceResponseSchema),
  ),
);

// NOTE: 닉네임 변경 서비스
export const updateNicknameService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(UpdateNicknameServiceRequestSchema),
    async (request: UpdateNicknameServiceRequest): Promise<UpdateNicknameServiceResponse> => {
      try {
        const { meta, data } = await putCuratorNickname({
          nickname: request.nickname,
        });

        if (meta?.result === 'FAIL') {
          throw new Error(meta.message ?? '닉네임 변경 중 에러가 발생했습니다.');
        }

        return {
          curatorId: data.curatorId,
          nickname: data.nickname,
        };
      } catch (error: unknown) {
        if (isApiError(error)) {
          throw new Error(error.data.meta.message);
        }

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        throw new Error('닉네임 변경 중 에러가 발생했습니다.');
      }
    },
    validateServiceResponse(UpdateNicknameServiceResponseSchema),
  ),
);

// NOTE: 닉네임 추천 서비스
export const getNicknameRecommendationService = withServiceErrorCapture(
  lazyPipe(async (): Promise<GetNicknameRecommendationServiceResponse> => {
    try {
      const { meta, data } = await getNicknameRecommendation();

      if (meta?.result === 'FAIL') {
        throw new Error(meta.message ?? '닉네임 추천 중 에러가 발생했습니다.');
      }

      return {
        nickname: data.nickname,
      };
    } catch (_error) {
      throw new Error('닉네임 추천 중 에러가 발생했습니다.');
    }
  }, validateServiceResponse(GetNicknameRecommendationServiceResponseSchema)),
);

export const getNicknameAvailableCountService = withServiceErrorCapture(
  lazyPipe(async (): Promise<GetNicknameAvailableCountServiceResponse> => {
    const { meta, data } = await getCuratorNicknameChangeCount();

    if (meta?.result === 'FAIL') {
      throw new Error(meta.message ?? '닉네임 변경 가능 횟수 조회 중 에러가 발생했습니다.');
    }

    return {
      maxCount: data.maxCount,
      remainingCount: data.remainingCount,
    };
  }, validateServiceResponse(GetNicknameAvailableCountServiceResponseSchema)),
);

export const updateSnsLinksService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(UpdateSnsLinksServiceRequestSchema),
    async (request: UpdateSnsLinksServiceRequest): Promise<UpdateSnsLinksServiceResponse> => {
      try {
        const { meta, data } = await putCuratorSns({
          snsLinks: request.snsLinks,
        });

        if (meta?.result === 'FAIL') {
          throw new Error(meta.message ?? 'SNS 링크 업데이트 중 에러가 발생했습니다.');
        }

        return {
          curatorId: data.curatorId,
        };
      } catch (error: unknown) {
        if (isApiError(error)) {
          throw new Error(error.data.meta.message);
        }

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        throw new Error('SNS 링크 업데이트 중 에러가 발생했습니다.');
      }
    },
    validateServiceResponse(UpdateSnsLinksServiceResponseSchema),
  ),
);

// NOTE: 닉네임 유효성 검사 서비스
export const validateNicknameService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(ValidateNicknameServiceRequestSchema),
    async (request: ValidateNicknameServiceRequest): Promise<ValidateNicknameServiceResponse> => {
      try {
        const { data, meta } = await getCuratorNicknameCheckDuplicate({
          nickname: request.nickname,
        });

        if (meta?.result === 'FAIL') {
          throw new Error(meta.message ?? '닉네임 유효성 검사 중 에러가 발생했습니다.');
        }

        return { isValid: data.available };
      } catch (error: unknown) {
        if (isApiError(error)) {
          throw new Error(error.data.meta.message);
        }

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        throw new Error('닉네임 유효성 검사 중 에러가 발생했습니다.');
      }
    },
    validateServiceResponse(ValidateNicknameServiceResponseSchema),
  ),
);

export const CURATION_SHOP_NOT_FOUND_ERROR_MESSAGE =
  '해당 큐레이터를 찾을 수 없거나 비활성 상태입니다.';

// NOTE: 방문자용 큐레이터 프로필 조회 서비스 (인증 없이 접근 가능)
export const getCuratorProfileBySnsTitleService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(GetCuratorProfileBySnsTitleServiceRequestSchema),
    async (
      request: GetCuratorProfileBySnsTitleServiceRequest,
    ): Promise<GetCuratorProfileBySnsTitleServiceResponse> => {
      try {
        const { meta, data } = await getCuratorProfileBySnsTitle({
          snsTitle: request.snsTitle,
        });

        if (meta?.result === 'FAIL') {
          throw new Error(meta.message ?? '큐레이터 프로필 조회 중 에러가 발생했습니다.');
        }

        const base = {
          nickname: data.nickname,
          profileImage: data.profileImage,
          shopDescription: data.shopDescription,
          snsLinks: data.snsLinks,
        };

        const response: GetCuratorProfileBySnsTitleServiceResponse =
          data.isMyShop === true
            ? {
                ...base,
                isMyShop: true,
                shopLink: data.shopLink ?? '',
              }
            : {
                ...base,
                isMyShop: false,
                shopLink: null,
              };

        return response;
      } catch (error: unknown) {
        if (isApiError(error)) {
          // 404 에러의 경우 더 구체적인 메시지 제공
          if (error.status === 404) {
            throw new Error(CURATION_SHOP_NOT_FOUND_ERROR_MESSAGE);
          }
          throw new Error(
            error.data?.meta?.message ?? '큐레이터 프로필 조회 중 에러가 발생했습니다.',
          );
        }

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        throw new Error('큐레이터 프로필 조회 중 에러가 발생했습니다.');
      }
    },
    validateServiceResponse(GetCuratorProfileBySnsTitleServiceResponseSchema),
  ),
);
